/**
 * Karaoke API client.
 *
 * Everything hits named /karaoke endpoints. Nothing goes through
 * /profile/getResult — that route runs arbitrary SQL from the request body with
 * no auth, and this feature does not build on it.
 */
const API = process.env.REACT_APP_API_END_POINT || 'https://gsapi.geetsuhane.com';
const TOKEN_KEY = 'gs_karaoke_token';

let pending = null;

async function asJson(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body;
}

function readToken() {
  try { return sessionStorage.getItem(TOKEN_KEY); } catch (e) { return null; }
}

function writeToken(token) {
  try { sessionStorage.setItem(TOKEN_KEY, token); } catch (e) { /* private mode */ }
}

function clearToken() {
  try { sessionStorage.removeItem(TOKEN_KEY); } catch (e) { /* private mode */ }
}

/**
 * The app keeps only `tmpgc` (a tmpid) in localStorage; the JWT the login
 * endpoints issue is discarded. Trade the tmpid for a scoped 12h karaoke token.
 */
export async function getToken({ force = false } = {}) {
  if (!force) {
    const existing = readToken();
    if (existing) return existing;
    if (pending) return pending;
  }

  const tmpid = localStorage.getItem('tmpgc');
  if (!tmpid) throw new Error('Sign in to record');

  pending = fetch(`${API}/karaoke/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tmpid }),
  })
    .then(asJson)
    .then((data) => { writeToken(data.token); return data.token; })
    .finally(() => { pending = null; });

  return pending;
}

async function authed(path, options = {}, retry = true) {
  const token = await getToken();
  const response = await fetch(`${API}${path}`, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (response.status === 401 && retry) {
    clearToken();
    await getToken({ force: true });
    return authed(path, options, false);
  }
  return asJson(response);
}

function query(params) {
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v || v === 0)).toString();
  return qs ? `?${qs}` : '';
}

export const listSongs = (params = {}) =>
  fetch(`${API}/karaoke/songs${query(params)}`).then(asJson);

export const getSong = (key) =>
  fetch(`${API}/karaoke/songs/${key}`).then(asJson);

export const listTakes = (params = {}) =>
  fetch(`${API}/karaoke/takes${query(params)}`).then(asJson);

export const listMyTakes = () => authed('/karaoke/takes/mine');

export function saveTake({ blob, songId, title, effect, syncMs, vocalGain, musicGain, duration, visibility }) {
  const form = new FormData();
  form.append('audio', blob, 'take.wav');
  form.append('song_id', songId);
  if (title) form.append('title', title);
  if (effect) form.append('effect', effect);
  form.append('sync_ms', String(Math.round(syncMs || 0)));
  form.append('vocal_gain', String(vocalGain != null ? vocalGain : 1));
  form.append('music_gain', String(musicGain != null ? musicGain : 1));
  form.append('duration', String(Math.round(duration || 0)));
  form.append('visibility', visibility || 'public');
  return authed('/karaoke/takes', { method: 'POST', body: form });
}

export const renderVideo = (takeId) =>
  authed(`/karaoke/takes/${takeId}/video`, { method: 'POST' });

export const shareTake = (takeId, text) =>
  authed(`/karaoke/takes/${takeId}/share`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });

export const likeTake = (takeId) =>
  authed(`/karaoke/takes/${takeId}/like`, { method: 'POST' });

export const deleteTake = (takeId) =>
  authed(`/karaoke/takes/${takeId}`, { method: 'DELETE' });

export default {
  getToken, listSongs, getSong, listTakes, listMyTakes,
  saveTake, renderVideo, shareTake, likeTake, deleteTake,
};
